import React, { useEffect, Fragment } from 'react'
import moment from 'moment'
import { Formik, Form } from 'formik'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import * as currentUserActionsCreators from '../../store/currentUserManager/currentUserManager'
import * as userManagerOperations from '../../store/css-admin/userManager/operations'
import * as notificationActionsCreators from '../../store/notifications'
import api from '../../lib/Api'
import { domains } from 'lib/config'
import { jobRolesLabel, jobRoles, schoolLookupLabel } from 'lib/meta'
import { userProfileSchema } from 'lib/schemas'
import AccountDetailsFirstTimeUserAndPromptToCompleteHeader from 'components/AccountDetailsFirstTimeUserAndPromptToCompleteHeader/AccountDetailsFirstTimeUserAndPromptToCompleteHeader'
import AccountDetailsFormikController from 'components/AccountDetailsFormikController'
import ErrorBoundary from 'components/ErrorBoundary'
import SchoolLookUp from 'components/SchoolLookUp'
import RegisteredTrademarkIcon from 'components/RegisteredTrademarkIcon'
import {
  Container,
  ErrorContainer,
  Header,
  Required,
  SchoolLookUpContainer,
  StyledHr,
  SubmitButton,
  SubmitSection,
} from './EditProfiles.styles'

const EditProfileForm = props => {
  const defaultProps = {
    profileTtlMin: 5,
  }
  const { addNotification, currentUser } = props
  const pathname = location.pathname
  const isFirstTimeUser = pathname === '/account-details/first-time'
  const promptToComplete = pathname === '/account-details/prompt-to-complete'
  const isSSO = currentUser.isSSO
  const buttonText = isFirstTimeUser || promptToComplete ? 'Finish' : 'Save'

  const doesJobRoleExist = jobRoles.some(
    jobRole => jobRole.id === currentUser.title,
  )
  const shouldResetTitle =
    (currentUser.title && !doesJobRoleExist) || promptToComplete

  const initialValues = {
    ...currentUser,
    ...(currentUser.hasInactiveNetSuiteCustomer && {
      companyName: '',
      region: '',
      city: '',
      country: '',
      nsCustomerId: '',
      netSuiteCustomerId: '',
    }),
    title: shouldResetTitle ? '' : currentUser.title,
    archetypes: promptToComplete
      ? []
      : (currentUser.archetypes || []).map(
          archetype => archetype.id || archetype,
        ),
  }

  useEffect(() => {
    const fetchDate = moment(props.currentUserFetchedAt)
    const refetchDate = fetchDate.add(
      props.profileTtlMin || defaultProps.profileTtlMin,
      'm',
    )
    if (moment() > refetchDate) {
      props.fetchCurrentUserAndHandler()
    }

    props.fetchAndHandleArchetypes()
  }, [])

  const handleSchoolChange = (details, setFieldValue) => {
    setFieldValue('country', details.country)
    setFieldValue('city', details.city)
    setFieldValue('region', details.region)
    setFieldValue('companyName', details.companyName)
    setFieldValue('nsCustomerId', details.nsCustomerId)
    setFieldValue('netsuiteCustomerId', details.netsuiteCustomerId)
    setFieldValue('organizationName', details.organizationName)
    setFieldValue('siteId', details.siteId)
  }

  const renderSchoolLookUp = (values, setFieldValue) => {
    const hasCompleteData =
      currentUser.city &&
      currentUser.country &&
      currentUser.companyName &&
      currentUser.netsuiteCustomerId !== 0

    // Show if not prompt-to-complete with complete data, OR if prompt-to-complete with complete data but missing values
    const shouldShow =
      !(promptToComplete && hasCompleteData) ||
      (promptToComplete && hasCompleteData && (!values.country || !values.city))

    if (!shouldShow) return null

    const isFallback =
      promptToComplete && hasCompleteData && (!values.country || !values.city)

    const siteData = {
      city: values.city || (isFallback ? currentUser.city : '') || '',
      country: values.country || (isFallback ? currentUser.country : '') || '',
      companyName:
        values.companyName || (isFallback ? currentUser.companyName : '') || '',
      netsuiteCustomerId:
        values.netsuiteCustomerId ||
        (isFallback ? currentUser.netsuiteCustomerId : null) ||
        null,
      nsCustomerId:
        values.nsCustomerId ||
        (isFallback ? currentUser.nsCustomerId : '') ||
        '',
      organizationName:
        values.organizationName ||
        (isFallback ? currentUser.organizationName : '') ||
        '',
      region: values.region || (isFallback ? currentUser.region : '') || '',
    }

    return (
      <SchoolLookUpContainer>
        <label>
          {schoolLookupLabel}
          {isFallback && ' (Update your information)'}
        </label>
        <SchoolLookUp
          fireOnLoad={false}
          onSelection={details => handleSchoolChange(details, setFieldValue)}
          siteData={siteData}
        />
      </SchoolLookUpContainer>
    )
  }

  const handleSubmit = async (values, actions) => {
    if (values.netsuiteCustomerId === 0)
      values.siteName = values.organizationName

    delete values.promptToComplete

    const reqData = {
      id: values.id,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      title: values.title,
      mobilePhoneNumber: values.mobilePhoneNumber || null,
      contactPhoneNumber: values.contactPhoneNumber || null,
      siteName: values.companyName || values.organizationName,
      netsuiteCustomerId: values.netsuiteCustomerId,
      country: values.country,
      region: values.region || null,
      city: values.city,
      archetypes: values.archetypes || [],
    }

    await api
      .put('/profile/me', reqData)
      .then(() => {
        actions.setSubmitting(false)
        actions.resetForm(values)
        addNotification({
          color: 'success',
          text: 'Successfully updated profile',
        })
        if (isFirstTimeUser || promptToComplete) {
          window.location = domains.CURRICULUM_APP_DOMAIN
        }
      })
      .catch(error => {
        actions.setSubmitting(false)
        const { response } = error
        const { data = '' } = response || {}
        const text = !data ? 'Failed to update profile' : data
        addNotification({
          color: 'danger',
          text: text,
        })
      })
  }

  return (
    <ErrorBoundary>
      <Container>
        {isFirstTimeUser || promptToComplete ? (
          <AccountDetailsFirstTimeUserAndPromptToCompleteHeader
            isFirstTimeUser={isFirstTimeUser}
            promptToComplete={promptToComplete}
          />
        ) : (
          <Header>Account Details</Header>
        )}
        <StyledHr />
        <Required>* Required</Required>
        {currentUser && (
          <Formik
            enableReinitialize={true}
            initialValues={Object.assign(initialValues, { promptToComplete })}
            onSubmit={handleSubmit}
            validationSchema={userProfileSchema}
          >
            {formikProps => {
              const { values, errors, dirty, setFieldValue } = formikProps

              // Base validation
              const hasNoErrors = !Object.keys(errors).length
              const hasTitle = values.title
              const hasArchetypes =
                values.archetypes && values.archetypes.length > 0

              // Name validation (conditional on SSO)
              const hasValidName =
                values.isSSO || (values.firstName && values.lastName)

              // Location validation (conditional on promptToComplete)
              const hasValidLocation =
                promptToComplete || (values.country && values.city)

              // Organization validation (conditional on netsuiteCustomerId and promptToComplete)
              const hasValidOrganization =
                promptToComplete ||
                values.netsuiteCustomerId === 0 ||
                values.organizationName ||
                (values.companyName && values.companyName.trim())

              const isValid =
                hasNoErrors &&
                hasTitle &&
                hasArchetypes &&
                hasValidName &&
                hasValidLocation &&
                hasValidOrganization

              return (
                <Form>
                  {!(isFirstTimeUser || promptToComplete) && (
                    <AccountDetailsFormikController
                      control="userDetails"
                      ErrorContainer={ErrorContainer}
                      errors={errors}
                      isSSO={isSSO}
                      names={['email', 'firstName', 'lastName']}
                      values={[values.email, values.firstName, values.lastName]}
                    />
                  )}
                  <AccountDetailsFormikController
                    control="radio"
                    ErrorContainer={ErrorContainer}
                    errors={errors.title}
                    jobRoles={jobRoles}
                    label={jobRolesLabel}
                    name="title"
                    value={values.title}
                  />
                  <AccountDetailsFormikController
                    archetypesData={props.archetypes}
                    control="checkbox"
                    ErrorContainer={ErrorContainer}
                    errors={errors.archetypes}
                    label={
                      <Fragment>
                        How are you involved with Second Step
                        <RegisteredTrademarkIcon /> programs? Select all that
                        apply:*
                      </Fragment>
                    }
                    name="archetypes"
                    setFieldValue={setFieldValue}
                    values={values.archetypes}
                  />
                  {renderSchoolLookUp(values, setFieldValue)}
                  <SubmitSection
                    isFirstTimeUser={isFirstTimeUser || promptToComplete}
                  >
                    <SubmitButton disabled={!isValid} type="submit">
                      {buttonText}
                    </SubmitButton>
                  </SubmitSection>
                </Form>
              )
            }}
          </Formik>
        )}
      </Container>
    </ErrorBoundary>
  )
}

function mapStateToProps({ currentUserManager, userManager }) {
  return {
    ...currentUserManager,
    archetypes: userManager.archetypes.data,
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      ...currentUserActionsCreators,
      ...userManagerOperations,
      ...notificationActionsCreators,
    },
    dispatch,
  )
}
export default connect(mapStateToProps, mapDispatchToProps)(EditProfileForm)
